import { useState, useEffect } from 'react';
import { debugLogger } from '@/utils/debugLogger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PlatformInfo {
  isIOS: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isAndroid: boolean;
}

export const usePWAInstall = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  
  // Platform detection
  const platform: PlatformInfo = {
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
    isChrome: /Chrome/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent)
  };

  useEffect(() => {
    // Log platform detection
    debugLogger.debug('PWA', 'Platform detection', platform);
    
    // Detectar se já está em modo standalone (PWA instalado)
    const checkStandalone = () => {
      const standalone = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      
      setIsStandalone(standalone);
      debugLogger.debug('PWA', 'Standalone mode detected', { standalone });
    };

    checkStandalone();

    // Capturar evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
      debugLogger.debug('PWA', 'Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detectar quando o app foi instalado
    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
      debugLogger.info('PWA', 'App installed successfully');
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Log initial state
    const initialState = {
      isInstallable,
      isStandalone,
      canShowInstallButton: !isStandalone && (isInstallable || platform.isIOS)
    };
    debugLogger.debug('PWA', 'Initial state', initialState);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstallable, isStandalone, platform.isIOS]);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      debugLogger.warn('PWA', 'No install prompt available');
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      debugLogger.info('PWA', 'Install prompt outcome', { outcome });
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      }
      return false;
    } catch (error) {
      debugLogger.error('PWA', 'Error showing install prompt', error);
      return false;
    }
  };

  return {
    isInstallable,
    isStandalone,
    promptInstall,
    platform,
    canShowInstallButton: !isStandalone && (isInstallable || platform.isIOS)
  };
};
